import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Reveal, Eyebrow } from "../components/primitives";
import CTABand from "../components/CTABand";
import Lightbox from "../components/Lightbox";
import { CASE_STUDIES, IMAGES, BUILT_FROM_SCRATCH, PROOF } from "../data/content";

function FeaturedRiysys() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);
  return (
    <section ref={ref} className="bg-ivory pt-36 md:pt-48" data-testid="work-hero">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20">
        <Reveal><Eyebrow>Selected Work</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h1 className="mt-6 font-serif font-light text-charcoal leading-[0.98] tracking-tight text-6xl md:text-8xl lg:text-[8vw]">
            The Work
          </h1>
        </Reveal>
      </div>

      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 mt-16 md:mt-24">
        <div className="relative overflow-hidden rounded-sm bg-taupe aspect-[16/10] md:aspect-[16/8]">
          <motion.img
            src={IMAGES.riysys}
            alt="RIYSYS at London Fashion Week"
            className="w-full h-full object-cover"
            style={{ y, scale: 1.15, objectPosition: "center 22%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />
          <div className="absolute bottom-0 left-0 p-8 md:p-14">
            <span className="font-sans text-xs uppercase tracking-[0.25em] text-gold">Featured</span>
            <h2 className="mt-3 font-serif font-light text-ivory text-3xl md:text-5xl lg:text-6xl leading-tight max-w-3xl">
              RIYSYS × London Fashion Week
            </h2>
          </div>
        </div>
        <Reveal delay={0.1}>
          <p className="mt-8 max-w-3xl font-sans text-base md:text-lg text-charcoal/70 leading-[1.75] font-light">
            Strategy partner for RIYSYS at London Fashion Week, steering the brand&rsquo;s positioning,
            creative and performance engine on one of fashion&rsquo;s biggest global stages.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function CaseStudy({ study, index }) {
  const dark = index % 2 === 1;
  const flip = index % 2 === 1;
  return (
    <section
      className={`${dark ? "bg-night text-ivory" : "bg-ivory text-charcoal"}`}
      data-testid={`case-study-${study.brand.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-24 items-center">
          <Reveal className={flip ? "md:order-2" : ""}>
            <div className="relative overflow-hidden rounded-sm bg-taupe aspect-[4/5] group">
              <img
                src={study.image}
                alt={study.brand}
                className="w-full h-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
              />
            </div>
          </Reveal>

          <Reveal delay={0.1} className={flip ? "md:order-1" : ""}>
            <span className="font-serif text-xl text-gold">{String(index + 1).padStart(2, "0")}</span>
            <h2 className={`mt-3 font-serif font-light leading-tight tracking-tight text-4xl md:text-6xl ${dark ? "text-ivory" : "text-charcoal"}`}>
              {study.brand}
            </h2>
            <div className="mt-10 grid grid-cols-2 gap-x-8 gap-y-8">
              {study.stats.map((st) => (
                <div key={st.label} className={`border-t pt-4 ${dark ? "border-ivory/20" : "border-taupe"}`}>
                  <div className={`font-serif text-3xl md:text-4xl ${dark ? "text-gold" : "text-charcoal"}`}>
                    {st.value}
                  </div>
                  <div className={`mt-2 font-sans text-xs uppercase tracking-[0.15em] ${dark ? "text-ivory/50" : "text-muted-foreground"}`}>
                    {st.label}
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function BuiltFromScratch() {
  return (
    <section className="bg-ivory" id="from-scratch" data-testid="built-from-scratch">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <Reveal><Eyebrow>From Zero</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-charcoal text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            Brands we built from nothing.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl font-sans text-base md:text-lg text-charcoal/70 leading-[1.75] font-light">
            Most agencies scale what is already working. We start where there is nothing.
            No sales, no ads, no proof. Then we build the engine that makes a brand run.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-taupe border border-taupe">
          {BUILT_FROM_SCRATCH.map((b, i) => (
            <Reveal key={b.brand} delay={(i % 3) * 0.08}>
              <div className="group h-full bg-ivory p-8 md:p-10 transition-colors duration-300 hover:bg-taupe/30">
                <div className="font-serif text-4xl md:text-5xl text-gold tracking-tight">{b.transform}</div>
                <h3 className="mt-5 font-serif text-2xl md:text-3xl text-charcoal font-normal">{b.brand}</h3>
                <p className="mt-3 font-sans text-sm text-charcoal/60 leading-[1.7]">{b.context}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal delay={0.1}>
          <p className="mt-16 text-center font-serif italic text-2xl md:text-3xl text-charcoal max-w-3xl mx-auto">
            Every one of these brands started at zero. That is the work we are built for.
          </p>
        </Reveal>
      </div>
    </section>
  );
}

function Proof() {
  const [active, setActive] = useState(null);
  return (
    <section className="bg-night text-ivory" data-testid="proof-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-20 py-28 md:py-40">
        <Reveal><Eyebrow>Real Accounts, Real Numbers</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-6 font-serif font-light text-ivory text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight max-w-4xl">
            The dashboards behind the results.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="mt-8 max-w-2xl font-sans text-base md:text-lg text-ivory/60 leading-[1.75] font-light">
            Not projections. Not estimates. Actual ad account and store performance from brands we scale.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-10">
          {PROOF.map((p, i) => (
            <Reveal key={p.caption} delay={(i % 2) * 0.1}>
              <figure>
                <button
                  onClick={() => setActive(p)}
                  data-testid={`proof-${i}`}
                  className="group block w-full overflow-hidden rounded-sm bg-ivory/95 shadow-[0_20px_60px_rgba(0,0,0,0.4)] cursor-zoom-in"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={p.img}
                      alt={p.caption}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                    />
                  </div>
                </button>
                <figcaption className="mt-4 font-sans text-xs uppercase tracking-[0.2em] text-gold">
                  {p.caption}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
      <Lightbox src={active?.img} alt={active?.caption} onClose={() => setActive(null)} />
    </section>
  );
}

export default function Work() {
  return (
    <>
      <FeaturedRiysys />
      <BuiltFromScratch />
      <Proof />
      <div className="mt-16 md:mt-24">
        {CASE_STUDIES.map((s, i) => (
          <CaseStudy key={s.brand} study={s} index={i} />
        ))}
      </div>
      <CTABand title="Your brand could be the next case study." variant="taupe" />
    </>
  );
}
